import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Divider, Grid, Typography } from "@mui/material";
import { groupBy, uniq } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createDiagnosticTemplate,
  getDiagnosticTemplates,
  getVitalGroupsDetailsByIdWoLoader,
  getVitalGroupsWoLoader,
  getVitalsByIdsWoLoader,
  updateDiagnosticTemplateById,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { addIcon, delBtn } from "../../../util/CommonIconStyle";
import { answerTypes, diagTemplateTypes } from "../../../util/dropList";
import { inputTypeEnum } from "../../../util/enum";
import { normalizeReportHtml } from "../../../util/function";
import ContentToPrint from "../../CommonScreens/ContentToPrint/ContentToPrint";

const initObject = {
  question: "",
  questionType: "checkboxes",
  isRequired: false,
  questionId: null,
  answers: [""],
};
const initValue = { name: "", type: "parameter", content: "" };
const ckEditorBuild =
  typeof ClassicEditor?.create === "function"
    ? ClassicEditor
    : ClassicEditor?.default;

const DiagnosticTemplateCreate = ({
  setCreateVsble,
  selectedTemp,
  setSelectedTemp,
}) => {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);
  const vitalSelectionInitRef = useRef(null);
  const pendingVitalGroupIdRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [vitalModVsble, setVitalModVsble] = useState(false);
  const [vitalValues, setVitalValues] = useState({});
  const [tempValues, setTempValues] = useState(initValue);
  const [questions, setQuestions] = useState([]);
  const [reportPrevModVsble, setReportPrevModVsble] = useState(false);
  const {
    vitalGroups,
    vitalListById,
    vitalsByIds,
    details: clinicDetails,
  } = useSelector((state) => state.clinic);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinic: clinicDataDet } = useMemo(() => {
    if (!mandateDataRaw) {
      return { clinic: {} };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
      };
    } catch {
      return { clinic: {} };
    }
  }, [mandateDataRaw]);
  const clinicDet = clinicDataDet?.id ? clinicDataDet : clinicDetails;

  useEffect(() => {
    if (fetchRef.current) return;
    fetchRef.current = true;
    dispatch(getVitalGroupsWoLoader());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedTemp) return;
    getAndSetDataFromSelectedPlan();
  }, [selectedTemp]);

  useEffect(() => {
    if (!vitalModVsble) {
      vitalSelectionInitRef.current = null;
      pendingVitalGroupIdRef.current = null;
      return;
    }

    const groupId = vitalValues?.group?.vitalGroupId;
    if (!groupId || pendingVitalGroupIdRef.current !== groupId) return;

    const list = Array.isArray(vitalListById) ? vitalListById : [];
    if (list.length === 0) return;

    const groupName = vitalValues?.group?.name ?? vitalValues?.group?.value;
    const listGroupName = list[0]?.groupName;
    if (groupName && listGroupName && listGroupName !== groupName) return;

    const initKey = String(groupId);
    if (vitalSelectionInitRef.current === initKey) return;

    const allIds = list.map((li) => li?.vitalId).filter(Boolean);
    const existingIds = (Array.isArray(questions) ? questions : [])
      ?.filter(
        (li) => li?.vitalGroupId === groupId && li?.vitalId && !li?.isDelete,
      )
      ?.map((li) => li.vitalId);
    const selectedIds = vitalValues?.isVitalEditMode ? existingIds : allIds;
    vitalSelectionInitRef.current = initKey;

    setVitalValues((prev) => ({
      ...prev,
      selectedVitalIds: selectedIds,
    }));
  }, [
    vitalModVsble,
    vitalListById,
    vitalValues?.group,
    vitalValues?.isVitalEditMode,
    questions,
  ]);

  const getAndSetDataFromSelectedPlan = () => {
    const tempValObj = {
      ...tempValues,
      name: selectedTemp?.templateName,
      type: selectedTemp?.type ?? "parameter",
      content: selectedTemp?.content ?? "",
    };
    setTempValues(tempValObj);
    if (
      !selectedTemp?.question ||
      selectedTemp?.question?.length === 0 ||
      selectedTemp?.type === "report"
    )
      return;
    const vitalIds = selectedTemp?.question
      ?.filter((li) => li?.vitalId)
      ?.map((li) => li?.vitalId);
    if (vitalIds?.length > 0) {
      dispatch(getVitalsByIdsWoLoader(vitalIds)).then((res) => {
        if (res?.payload) {
          const vitalList = res?.payload ?? [];
          const reqList = selectedTemp?.question?.map((li, i) => {
            let reqObj = {
              question: li?.question,
              questionType: li?.questionType,
              questionId: li?.questionId,
              required: li?.required,
              isRequired: li?.required,
            };
            if (li?.vitalGroupId) reqObj.vitalGroupId = li?.vitalGroupId;
            if (li?.vitalId) {
              const matchObj = vitalList?.find(
                (vli) => vli?.vitalId === li?.vitalId,
              );
              if (matchObj) {
                reqObj = { ...(matchObj ?? {}), ...(reqObj ?? {}) };
                if (matchObj.groupName)
                  reqObj.vitalGroupName = matchObj.groupName;
              }
              reqObj.vitalId = li?.vitalId;
            }
            if (
              li?.questionType === "multipleChoice" ||
              li?.questionType === "checkboxes" ||
              li?.questionType === "fileupload"
            ) {
              reqObj.answers =
                li?.questionType === "fileupload" ? ["file"] : li?.answer;
            }
            if (
              li?.questionType === "shortAnswer" ||
              li?.questionType === "paragraph"
            ) {
              reqObj.answer = Array.isArray(li?.answer)
                ? li?.answer?.[0]
                : (li?.answer ?? "");
            }
            return reqObj;
          });
          setQuestions(reqList);
        }
      });
      return;
    }
    const reqList = selectedTemp?.question?.map((li, i) => {
      const reqObj = {
        question: li?.question,
        questionType: li?.questionType,
        questionId: li?.questionId,
        required: li?.required,
        isRequired: li?.required,
      };
      if (li?.vitalGroupId) reqObj.vitalGroupId = li?.vitalGroupId;
      if (li?.vitalId) reqObj.vitalId = li?.vitalId;
      if (
        li?.questionType === "multipleChoice" ||
        li?.questionType === "checkboxes" ||
        li?.questionType === "fileupload"
      ) {
        reqObj.answers =
          li?.questionType === "fileupload" ? ["file"] : li?.answer;
      }
      if (
        li?.questionType === "shortAnswer" ||
        li?.questionType === "paragraph"
      ) {
        reqObj.answer = li?.answer ?? "";
      }
      return reqObj;
    });
    setQuestions(reqList);
  };

  const handleChange = (name, value) => {
    const reqObj = { ...tempValues, [name]: value };
    if (name === "name") {
      reqObj.error = value?.length > 0 ? false : true;
    }
    setTempValues(reqObj);
  };

  const moveArrayElement = (array, fromIndex, toIndex) => {
    const updatedArray = [...array];
    const [movedItem] = updatedArray.splice(fromIndex, 1); // Remove the item from the old index
    updatedArray.splice(toIndex, 0, movedItem); // Insert it at the new index
    return updatedArray;
  };

  const onSortEnd = (oldIndex, newIndex) => {
    setQuestions((array) => moveArrayElement(array, oldIndex, newIndex));
  };

  const handleChangeValue = (name, value, ind) => {
    const vitalList = questions?.filter((li) => li?.vitalId);
    const reqList = questions
      ?.filter((li) => !li?.isDelete && !li?.vitalId)
      ?.map((li, i) => {
        if (i === ind) {
          const reqObj = { ...li, [name]: value };
          if (
            value === "multipleChoice" ||
            value === "checkboxes" ||
            value === "fileupload"
          ) {
            reqObj.answers = [value === "fileupload" ? "file" : ""];
          }
          if (value === "shortAnswer" || value === "paragraph") {
            reqObj.answer = "";
          }
          return reqObj;
        }
        return li;
      });
    setQuestions([...(vitalList ?? []), ...(reqList ?? [])]);
  };

  const handleChangeAnswer = (mainInd, secInd, value) => {
    const vitalList = questions?.filter((li) => li?.vitalId);
    const reqList = questions
      ?.filter((li) => !li?.isDelete && !li?.vitalId)
      ?.map((li, i) => {
        if (i === mainInd) {
          const reqArr = li?.answers?.map((ans, ind) => {
            if (ind === secInd) return value;
            return ans;
          });
          return { ...li, answers: reqArr };
        }
        return li;
      });
    setQuestions([...(vitalList ?? []), ...(reqList ?? [])]);
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [...prev, initObject]);
  };

  const handleDeleteQuestion = (ind) => {
    const vitalList = (Array.isArray(questions) ? questions : []).filter(
      (li) => li?.vitalId,
    );
    const reqQues = (Array.isArray(questions) ? questions : [])
      .filter((li) => !li?.isDelete && !li?.vitalId)
      .filter((_item, i) => i !== ind)
      .map((cl, i) => ({ ...cl, i }));
    setQuestions([...vitalList, ...reqQues]);
  };

  const handleBackClick = () => {
    dispatch(getDiagnosticTemplates(""));
    setTempValues(initValue);
    setCreateVsble(false);
    setSelectedTemp(null);
    setActiveStep(0);
  };

  const handleAddAnswer = (ind) => {
    const vitalList = questions?.filter((li) => li?.vitalId);
    const reqList = questions
      ?.filter((li) => !li?.isDelete && !li?.vitalId)
      ?.map((li, i) => {
        if (i === ind) {
          return { ...li, answers: [...li?.answers, ""] };
        }
        return li;
      });
    setQuestions([...(vitalList ?? []), ...(reqList ?? [])]);
  };

  const handleDeleteAnswer = (mainInd, secInd) => {
    const vitalList = questions?.filter((li) => li?.vitalId);
    const reqList = questions
      ?.filter((li) => !li?.isDelete && !li?.vitalId)
      ?.map((li, i) => {
        if (i === mainInd) {
          const reqArr = li?.answers?.filter((ans, ind) => ind !== secInd);
          return { ...li, answers: reqArr };
        }
        return li;
      });
    setQuestions([...(vitalList ?? []), ...(reqList ?? [])]);
  };

  const handleNext = () => {
    if (tempValues?.name?.length === 0 || !tempValues?.name) {
      return setTempValues({ ...tempValues, error: true });
    }
    if (tempValues?.type === "report") {
      return setReportPrevModVsble(true);
    }
    const activeQuestions = (Array.isArray(questions) ? questions : []).filter(
      (li) => !li?.isDelete,
    );
    if (
      activeQuestions.length === 0 ||
      activeQuestions.some(
        (li) =>
          !(typeof li?.question === "string"
            ? li.question.length > 0
            : !!li?.question) || !li?.questionType,
      )
    )
      return;
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = () => {
    const question = (Array.isArray(questions) ? questions : [])
      .filter((ques) => !ques?.isDelete)
      .map((ques) => {
        const reqObj = {
          question: ques?.question ?? "",
          questionType: ques?.questionType ?? "",
          required: ques?.isRequired ?? false,
        };
        if (ques?.inputType === "Numeric") {
          reqObj.unit = ques?.unit ?? "";
          reqObj.lowerRange = ques?.lowerRange ?? "";
          reqObj.upperRange = ques?.upperRange ?? "";
          reqObj.inputType = ques?.inputType;
        }
        if (
          ques?.questionType === "shortAnswer" ||
          ques?.questionType === "paragraph"
        ) {
          reqObj.answer = Array.isArray(ques?.answer)
            ? ques?.answer
            : [ques?.answer ?? ""];
        }
        if (
          ques?.questionType === "multipleChoice" ||
          ques?.questionType === "checkboxes"
        ) {
          reqObj.answer = Array.isArray(ques?.answers) ? ques.answers : [];
        }
        if (ques?.questionType === "fileupload") {
          reqObj.answer = Array.isArray(ques?.answers) ? ques.answers : [];
        }
        if (ques?.inputType === "Numeric" || ques?.inputType === "TextBox") {
          reqObj.answer = [""];
        }
        if (ques?.questionId) reqObj.questionId = ques.questionId;
        if (ques?.vitalGroupId || ques?.vitalId) {
          reqObj.vitalGroupId = ques?.vitalGroupId;
          reqObj.vitalId = ques?.vitalId;
        }
        if (ques?.inputType === "Numeric") {
          reqObj.unit = ques?.unit ?? "";
          reqObj.lowerRange = ques?.lowerRange ?? "";
          reqObj.upperRange = ques?.upperRange ?? "";
          reqObj.inputType = ques?.inputType;
        }
        return reqObj;
      });
    const data = {
      templateName: tempValues?.name,
      question,
      content: tempValues?.content,
      type: tempValues?.type,
    };
    if (selectedTemp?.templateId) {
      dispatch(
        updateDiagnosticTemplateById({
          tempId: selectedTemp?.templateId,
          data,
        }),
      ).then((res) => {
        if (res?.payload) handleBackClick();
      });
    } else {
      dispatch(createDiagnosticTemplate(data)).then((res) => {
        if (res?.payload) handleBackClick();
      });
    }
  };

  const handleAddVital = () => {
    vitalSelectionInitRef.current = null;
    pendingVitalGroupIdRef.current = null;
    setVitalValues({
      group: null,
      selectedVitalIds: [],
      isVitalEditMode: false,
    });
    setVitalModVsble(true);
  };

  const onCloseVitalMod = () => {
    vitalSelectionInitRef.current = null;
    pendingVitalGroupIdRef.current = null;
    setVitalModVsble(false);
    setVitalValues({});
  };

  const getVitalGroups = () => {
    return (Array.isArray(vitalGroups) ? vitalGroups : []).map((item) => ({
      name: item?.groupName,
      value: item?.groupName,
      vitalGroupId: item?.roomId,
    }));
  };

  const getExistingVitalIdsForGroup = (groupId) => {
    if (!groupId) return [];
    return (Array.isArray(questions) ? questions : [])
      ?.filter(
        (li) => li?.vitalGroupId === groupId && li?.vitalId && !li?.isDelete,
      )
      ?.map((li) => li.vitalId);
  };

  const fetchVitalGroupDetails = (groupId) => {
    if (!groupId) return;
    pendingVitalGroupIdRef.current = groupId;
    vitalSelectionInitRef.current = null;
    dispatch(getVitalGroupsDetailsByIdWoLoader(groupId));
  };

  const onChange = (name, value) => {
    if (name === "group") {
      if (value?.vitalGroupId) {
        fetchVitalGroupDetails(value.vitalGroupId);
      } else {
        pendingVitalGroupIdRef.current = null;
        vitalSelectionInitRef.current = null;
      }
      setVitalValues({
        group: value,
        selectedVitalIds: [],
        isVitalEditMode: vitalValues?.isVitalEditMode ?? false,
        error: value?.vitalGroupId ? false : true,
      });
      return;
    }
    setVitalValues({
      ...vitalValues,
      [name]: value,
    });
  };

  const toggleVitalSelection = (vitalId, nextChecked) => {
    if (!vitalId) return;
    const current = Array.isArray(vitalValues?.selectedVitalIds)
      ? vitalValues.selectedVitalIds
      : [];
    const selectedVitalIds = nextChecked
      ? [...new Set([...current, vitalId])]
      : current.filter((id) => id !== vitalId);
    setVitalValues({
      ...vitalValues,
      selectedVitalIds,
    });
  };

  const toggleSelectAllVitals = (nextChecked) => {
    const list = Array.isArray(vitalListById) ? vitalListById : [];
    setVitalValues({
      ...vitalValues,
      selectedVitalIds: nextChecked
        ? list.map((li) => li?.vitalId).filter(Boolean)
        : [],
    });
  };

  const buildVitalQuestion = (vl, existingQues = null) => {
    if (!vl || typeof vl !== "object") return null;
    if (existingQues && typeof existingQues === "object") {
      return {
        ...existingQues,
        ...vl,
        question: existingQues?.question || vl?.vitalName || "",
        vitalId: vl?.vitalId,
        vitalGroupId:
          vitalValues?.group?.vitalGroupId ?? existingQues?.vitalGroupId,
        vitalGroupName: vl?.groupName ?? existingQues?.vitalGroupName,
        inputType: vl?.inputType ?? existingQues?.inputType,
        questionId: existingQues?.questionId ?? null,
      };
    }
    return {
      ...vl,
      question: vl?.vitalName ?? "",
      questionType:
        vl?.inputType === "TextBox" || vl?.inputType === "Numeric"
          ? "shortAnswer"
          : "multipleChoice",
      isRequired: false,
      questionId: null,
      answers:
        vl?.inputType === "TextBox" || vl?.inputType === "Numeric"
          ? ""
          : Array.isArray(vl?.dropdown)
            ? vl.dropdown
            : [],
      vitalId: vl?.vitalId,
      vitalGroupId: vitalValues?.group?.vitalGroupId,
      vitalGroupName: vl?.groupName,
      inputType: vl?.inputType,
    };
  };

  const handleAddVitalGroup = () => {
    try {
      const groupId = vitalValues?.group?.vitalGroupId;
      if (!groupId) {
        setVitalValues({ ...(vitalValues ?? {}), error: true });
        return;
      }

      const selectedIds = Array.isArray(vitalValues?.selectedVitalIds)
        ? vitalValues.selectedVitalIds.filter(Boolean)
        : [];
      const isEdit = isUpdatingVitalGroup();
      if (selectedIds.length === 0 && !isEdit) return;

      const list = Array.isArray(vitalListById) ? vitalListById : [];
      const selectedVitals = list.filter(
        (vl) => vl?.vitalId && selectedIds.includes(vl.vitalId),
      );
      if (selectedVitals.length === 0 && !isEdit) return;

      const currentQuestions = Array.isArray(questions) ? questions : [];
      const otherQuestions = currentQuestions.filter(
        (li) => li?.vitalGroupId !== groupId,
      );

      const vitalQuestions = selectedVitals
        .map((vl) => {
          if (!vl?.vitalId) return null;
          const existing =
            currentQuestions.find(
              (li) =>
                li?.vitalId === vl.vitalId &&
                li?.vitalGroupId === groupId &&
                !li?.isDelete,
            ) ?? null;
          return buildVitalQuestion(vl, existing);
        })
        .filter(Boolean);

      setQuestions([...vitalQuestions, ...otherQuestions]);
      setVitalModVsble(false);
      setVitalValues({});
    } catch {
      setVitalModVsble(false);
      setVitalValues({});
    }
  };

  const isUpdatingVitalGroup = () => vitalValues?.isVitalEditMode === true;

  const getUniqVitalGroupIds = () => {
    try {
      const reqList = (Array.isArray(questions) ? questions : [])
        .filter((li) => li?.vitalGroupId && !li?.isDelete)
        .map((li) => li.vitalGroupId);
      return uniq(reqList);
    } catch {
      return [];
    }
  };

  const getGroupByVitals = () => {
    try {
      const reqQuestions = (Array.isArray(questions) ? questions : []).filter(
        (li) => (li?.vitalId || li?.vitalGroupId) && !li?.isDelete,
      );
      return groupBy(reqQuestions, "vitalGroupId") ?? {};
    } catch {
      return {};
    }
  };

  const handleDeleteVitalGroup = (id) => {
    if (!id) return;
    try {
      const reqList = (Array.isArray(questions) ? questions : []).filter(
        (li) => li?.vitalGroupId !== id,
      );
      setQuestions(reqList);
    } catch {
      // ignore malformed question list
    }
  };

  const handleEditVitalGroup = (groupId, groupName) => {
    if (!groupId) return;
    const group = {
      name: groupName,
      value: groupName,
      vitalGroupId: groupId,
    };
    fetchVitalGroupDetails(groupId);
    setVitalValues({
      group,
      selectedVitalIds: getExistingVitalIdsForGroup(groupId),
      isVitalEditMode: true,
      error: false,
    });
    setVitalModVsble(true);
  };

  return (
    <>
      <div className="ml50 cursor mt10" onClick={handleBackClick}>
        <div className="flex-row-ali-cen">
          <ArrowBackIosIcon
            sx={{ color: AppColors.appPrimary, width: 20, height: 20 }}
          />
          <Typography className="fs16 txt-semi-bold blue-color ml5">
            Back
          </Typography>
        </div>
      </div>
      <Grid container spacing={0} className="flex-center mt20">
        <Grid item xs={12} sm={12} md={10} lg={10} xl={10}>
          <div className="scroll-70vh pr10 pb30">
            {activeStep === 0 ? (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <div className="txt-semi-bold fs14 blue-color">
                      Template Name <span className="red-color">*</span>
                    </div>
                    <CustomTextField
                      value={tempValues?.name}
                      handleChange={(e) =>
                        handleChange("name", e?.target?.value)
                      }
                      fullWidth
                      error={tempValues?.error}
                      helperText={
                        tempValues?.error ? "This field is required" : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div className="txt-semi-bold fs14 blue-color">
                      Type <span className="red-color">*</span>
                    </div>
                    <CustomSelect
                      value={tempValues?.type}
                      select
                      disabled
                      list={diagTemplateTypes}
                    />
                  </Grid>
                </Grid>
                {tempValues?.type === "parameter" ? (
                  <>
                    {getUniqVitalGroupIds()?.length > 0 ? (
                      <div>
                        {getUniqVitalGroupIds()?.map((grp, i) => (
                          <div key={`${grp}_${i}`} className="mt15">
                            <div className="flex-row-ali-cen">
                              <div className="blue-color fs16 txt-semi-bold">
                                {getGroupByVitals()?.[grp]?.[0]?.vitalGroupName}
                              </div>
                              <div
                                className="cursor ml15"
                                onClick={() =>
                                  handleEditVitalGroup(
                                    getGroupByVitals()?.[grp]?.[0]
                                      ?.vitalGroupId,
                                    getGroupByVitals()?.[grp]?.[0]
                                      ?.vitalGroupName,
                                  )
                                }
                              >
                                <div className="flex-center">
                                  <EditOutlinedIcon
                                    sx={{
                                      color: AppColors.appPrimary,
                                      width: 18,
                                      height: 18,
                                    }}
                                  />
                                </div>
                                <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                                  Edit
                                </Typography>
                              </div>
                              <div
                                className="cursor ml15"
                                onClick={() =>
                                  handleDeleteVitalGroup(
                                    getGroupByVitals()?.[grp]?.[0]
                                      ?.vitalGroupId,
                                  )
                                }
                              >
                                <div className="flex-center">
                                  <DeleteOutlineIcon sx={delBtn} />
                                </div>
                                <Typography className="red3 fs8 txt-semi-bold text-align-center">
                                  Delete
                                </Typography>
                              </div>
                            </div>
                            <div className="flex-row-ali-cen ph20 mt10">
                              <div className="w30Per">
                                <Typography className="font-bold blue-color fs12">
                                  Parameter Name
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Input Type
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Unit
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Reference Range
                                </Typography>
                              </div>
                            </div>
                            <div className="blue-hor-split mt5" />
                            {getGroupByVitals()?.[grp]?.map((vqu, ind) => {
                              // const reqVit = vitalListById?.find(
                              //   (vli) => vli?.vitalId === vqu?.vitalId,
                              // );

                              return (
                                <div
                                  className="flex-row-ali-cen mt5 ph20"
                                  key={`${vqu?.vitalId}_${ind}`}
                                >
                                  <div className="w30Per txt-semi-bold fs12 black">
                                    {vqu?.question}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {inputTypeEnum[vqu?.inputType]}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {vqu?.unit?.length > 0
                                      ? ` ${vqu?.unit}`
                                      : ""}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {vqu?.unit?.length > 0 &&
                                      `${vqu?.lowerRange ?? ""} - ${vqu?.upperRange ?? ""}`}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <SortableList
                      onSortEnd={onSortEnd}
                      className="parent-list"
                      dragHandleSelector=".parent-drag-handle"
                      tag="div"
                    >
                      {questions
                        ?.filter((li) => !li?.isDelete && !li?.vitalId)
                        ?.map((ques, i) => {
                          return (
                            <SortableItem key={`${i}-parent`}>
                              <Grid container spacing={1} className="mt10">
                                {/* <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                              <Typography className="fs16 blue-color font-medium">
                                Title Name {i + 1}
                              </Typography>
                            </Grid> */}
                                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                                  <CustomTextField
                                    fullWidth
                                    handleChange={(e) =>
                                      handleChangeValue(
                                        "question",
                                        e?.target?.value,
                                        i,
                                      )
                                    }
                                    value={ques?.question}
                                    placeholder="Type title here"
                                    disabled={
                                      ques?.vitalGroupId && ques?.vitalId
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                  <CustomSelect
                                    list={answerTypes}
                                    value={ques?.questionType}
                                    handleChange={(e) =>
                                      handleChangeValue(
                                        "questionType",
                                        e?.target?.value,
                                        i,
                                      )
                                    }
                                    select
                                    disabled={
                                      ques?.vitalGroupId && ques?.vitalId
                                    }
                                  />
                                </Grid>
                                {ques?.questionType === "multipleChoice" ||
                                ques?.questionType === "checkboxes" ? (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                  >
                                    {ques?.answers?.map((ans, ind) => (
                                      <div className="flex-row-ali-cen">
                                        <div className="ml10">
                                          <CustomCheckbox
                                            value={false}
                                            circle={
                                              ques?.questionType ===
                                              "multipleChoice"
                                            }
                                            disabled={
                                              ques?.vitalGroupId &&
                                              ques?.vitalId
                                            }
                                          />
                                        </div>
                                        <div className="w60Per">
                                          <CustomTextField
                                            value={ans}
                                            fullWidth
                                            handleChange={(e) =>
                                              handleChangeAnswer(
                                                i,
                                                ind,
                                                e?.target?.value,
                                              )
                                            }
                                            disabled={
                                              ques?.vitalGroupId &&
                                              ques?.vitalId
                                            }
                                          />
                                        </div>
                                        {ques?.answers?.length === ind + 1 &&
                                        !(
                                          ques?.vitalGroupId && ques?.vitalId
                                        ) ? (
                                          <img
                                            src={require("../../../assets/images/png/plusGry.png")}
                                            className="img-wh20 ml10 cursor"
                                            onClick={() => handleAddAnswer(i)}
                                          />
                                        ) : null}
                                        {ques?.answers?.length > 1 &&
                                        !(
                                          ques?.vitalGroupId && ques?.vitalId
                                        ) ? (
                                          <img
                                            src={require("../../../assets/images/png/delGry.png")}
                                            className="img-wh20 ml10 cursor"
                                            onClick={() =>
                                              handleDeleteAnswer(i, ind)
                                            }
                                          />
                                        ) : null}
                                      </div>
                                    ))}
                                  </Grid>
                                ) : ques?.questionType === "paragraph" ||
                                  ques?.questionType === "shortAnswer" ? (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                  >
                                    <CustomTextField
                                      value={ques?.answer}
                                      fullWidth
                                      handleChange={(e) =>
                                        handleChangeValue(
                                          "answer",
                                          e?.target?.value,
                                          i,
                                        )
                                      }
                                      disabled={
                                        ques?.vitalGroupId && ques?.vitalId
                                      }
                                      multiline={
                                        ques?.questionType === "paragraph"
                                      }
                                    />
                                  </Grid>
                                ) : null}
                                <Grid
                                  item
                                  xs={12}
                                  sm={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                >
                                  <div className="flex-row-between-align-center mt10">
                                    <div className="flex-row-ali-cen">
                                      <div className="cursor">
                                        <span className="parent-drag-handle">
                                          ☰
                                        </span>
                                      </div>
                                      <img
                                        src={require("../../../assets/images/png/del.png")}
                                        className="img-wh20 cursor ml10"
                                        onClick={() => handleDeleteQuestion(i)}
                                      />
                                    </div>
                                    <div className="pr10 flex-center">
                                      <CustomSwitch
                                        value={ques?.isRequired}
                                        onChange={(e) =>
                                          handleChangeValue(
                                            "isRequired",
                                            e?.target?.value,
                                            i,
                                          )
                                        }
                                        grayToBlue
                                        disabled={
                                          ques?.vitalGroupId && ques?.vitalId
                                        }
                                      />
                                    </div>
                                  </div>
                                </Grid>
                              </Grid>
                            </SortableItem>
                          );
                        })}
                    </SortableList>
                    <div className="flex-row-ali-cen mt20">
                      <div
                        className="flex-row-ali-cen cursor"
                        onClick={handleAddQuestion}
                      >
                        <div className="flex-center plus-icon">
                          <AddIcon sx={addIcon} />
                        </div>
                        <Typography className="blue-color fs16 ml5 font-bold">
                          Add Field
                        </Typography>
                      </div>

                      <div
                        className="flex-row-ali-cen cursor ml15"
                        onClick={handleAddVital}
                      >
                        <div className="flex-center plus-icon">
                          <AddIcon sx={addIcon} />
                        </div>
                        <Typography className="blue-color fs16 ml5 font-bold">
                          Add Parameter
                        </Typography>
                      </div>
                    </div>
                  </>
                ) : null}

                {tempValues?.type === "report" ? (
                  ckEditorBuild ? (
                    <div className="mv20">
                      <CKEditor
                        editor={ckEditorBuild}
                        disableWatchdog
                        data={tempValues?.content}
                        onChange={(event, editor) => {
                          const value = editor?.getData();
                          handleChange("content", value);
                        }}
                      />
                    </div>
                  ) : null
                ) : null}

                <div className="flex-end mt20">
                  <div>
                    <CustomButton text="Next" smallBtn onClick={handleNext} />
                  </div>
                </div>
              </div>
            ) : null}
            {activeStep === 1 ? (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Typography className="blue-color txt-semi-bold flex-center fs20">
                      Review & Submit
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <div className="txt-semi-bold fs14 blue-color">
                      Template Name
                    </div>
                    <CustomTextField
                      value={tempValues?.name}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div className="txt-semi-bold fs14 blue-color">Type</div>
                    <CustomSelect
                      value={tempValues?.type}
                      select
                      disabled
                      list={diagTemplateTypes}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    {getUniqVitalGroupIds()?.length > 0 ? (
                      <div>
                        {getUniqVitalGroupIds()?.map((grp, i) => (
                          <div key={`${grp}_${i}`} className="mt15">
                            <div className="flex-row-ali-cen">
                              <div className="blue-color fs16 txt-semi-bold">
                                {getGroupByVitals()?.[grp]?.[0]?.vitalGroupName}
                              </div>
                            </div>
                            <div className="flex-row-ali-cen ph20 mt10">
                              <div className="w30Per">
                                <Typography className="font-bold blue-color fs12">
                                  Parameter Name
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Input Type
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Unit
                                </Typography>
                              </div>
                              <div className="w20Per">
                                <Typography className="font-bold blue-color fs12">
                                  Reference Range
                                </Typography>
                              </div>
                            </div>
                            <div className="blue-hor-split mt5" />
                            {getGroupByVitals()?.[grp]?.map((vqu, ind) => {
                              // const reqVit = vitalListById?.find(
                              //   (vli) => vli?.vitalId === vqu?.vitalId,
                              // );

                              return (
                                <div
                                  className="flex-row-ali-cen mt5 ph20"
                                  key={`${vqu?.vitalId}_${ind}`}
                                >
                                  <div className="w30Per txt-semi-bold fs12 black">
                                    {vqu?.question}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {inputTypeEnum[vqu?.inputType]}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {vqu?.unit?.length > 0
                                      ? ` ${vqu?.unit}`
                                      : ""}
                                  </div>
                                  <div className="w20Per font-medium fs12 gray3">
                                    {vqu?.unit?.length > 0 &&
                                      `${vqu?.lowerRange ?? ""} - ${vqu?.upperRange ?? ""}`}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {questions
                      ?.filter((li) => !li?.isDelete && !li?.vitalId)
                      ?.map((ques, i) => (
                        <div key={`${ques?.question} ${i}`} className="mv5">
                          <Typography className="font-medium fs16 black">
                            {/* {i + 1})  */}
                            {ques?.question}
                          </Typography>
                          <div className="mv5">
                            {ques?.questionType === "multipleChoice" ||
                            ques?.questionType === "checkboxes" ? (
                              <div className="ml10">
                                {ques?.answers?.map((ans) => (
                                  <div className="flex-row-ali-cen">
                                    <CustomCheckbox
                                      value={false}
                                      circle={
                                        ques?.questionType === "multipleChoice"
                                      }
                                      disabled
                                    />
                                    <Typography className="mlMin15 font-medium fs14 black">
                                      {ans}
                                    </Typography>
                                  </div>
                                ))}
                              </div>
                            ) : ques?.questionType === "paragraph" ||
                              ques?.questionType === "shortAnswer" ? (
                              <Typography className="font-medium fs14 black">
                                {ques?.answer}
                              </Typography>
                            ) : null}
                          </div>
                        </div>
                      ))}
                  </Grid>
                </Grid>
                <div className="flex-end">
                  <div>
                    <CustomButton
                      text="Back"
                      smallBtn
                      grayBtn
                      onClick={handleBack}
                    />
                  </div>

                  <div className="ml20">
                    <CustomButton
                      text="Submit"
                      smallBtn
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </Grid>
      </Grid>
      <CustomModal
        open={reportPrevModVsble}
        onClose={() => setReportPrevModVsble(false)}
        header=""
        modal
        modalWidth={90}
      >
        <div className="scroll-80vh">
          <ContentToPrint
            footer={
              <div className="prescription-print-footer-inner">
                <div className="mb20 flex-start prescription-print-footer-doctor-row">
                  <div>
                    <div className="txt-semi-bold fs12 black">
                      Clinician Signature
                    </div>
                    {/* <div className="prescription-print-footer-doctor-name txt-semi-bold fs12 black">
                      Signature
                    </div> */}
                    {/* <div className="font-medium fs12 black">
                      Signature of Veterinarian
                    </div> */}
                  </div>
                </div>

                {clinicDet?.footer?.length > 0 ? (
                  <img src={clinicDet?.footer} alt="" />
                ) : null}
              </div>
            }
            header={
              <div className="flex-row-ali-cen mv20">
                <div className="w35Per">
                  <div>
                    {clinicDet?.image ? (
                      <img src={clinicDet?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {clinicDet?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w30Per flex-center">
                  <div className="font-bold fs20 blue-color">
                    {tempValues?.name}
                  </div>
                </div>
                <div className="w35Per flex1-end">
                  <div className="prescription-doc-header-clinic">
                    {/* <div className="font-bold fs12 blue-color">
                      Business Name:
                    </div> */}
                    <div className="font-bold blue-color fs16">
                      {clinicDet?.name ?? ""}
                    </div>
                    {/* <div className="font-bold fs12 blue-color mt5">
                      Business Address:
                    </div> */}
                    <div className="txt-regular fs12 blue-color">
                      {clinicDet?.address ?? ""}
                    </div>
                    {clinicDet?.taxId?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          {/* CLINIC  */}
                          TAX ID:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.taxId ?? ""}
                        </div>
                      </div>
                    ) : null}
                    {clinicDet?.mobile?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          Contact:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.mobile ?? ""}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          >
            <div
              className="diagnostic-report-rich-content"
              dangerouslySetInnerHTML={{
                __html: normalizeReportHtml(tempValues?.content),
              }}
            />
          </ContentToPrint>

          <div className="flex1-end mv20">
            <div>
              <CustomButton smallBtn text="Submit" onClick={handleSubmit} />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={vitalModVsble}
        onClose={onCloseVitalMod}
        header={isUpdatingVitalGroup() ? "Update Parameter" : "Add Parameter"}
        rightModal
        modalWidth={30}
      >
        <div className="ph20 scroll-80vh">
          <Typography className="blue-color fs14 txt-semi-bold">
            Parameter Group
          </Typography>
          <div className="mt10">
            <CustomSelect
              list={getVitalGroups()}
              value={vitalValues?.group}
              handleChange={(e, value) => onChange("group", value)}
              search
              error={vitalValues?.error}
              helperText={vitalValues?.error ? "This field is required" : ""}
            />
          </div>
          <div className="mt10">
            {vitalValues?.group?.vitalGroupId &&
            Array.isArray(vitalListById) &&
            vitalListById.length > 0 ? (
              <>
                <Typography className="blue-color fs14 txt-semi-bold">
                  Parameter Group Name: {vitalListById?.[0]?.groupName}
                </Typography>
                <div className="mt10">
                  <CustomCheckbox
                    label="Select All"
                    checked={
                      vitalListById.length > 0 &&
                      vitalListById.every((li) =>
                        (Array.isArray(vitalValues?.selectedVitalIds)
                          ? vitalValues.selectedVitalIds
                          : []
                        ).includes(li?.vitalId),
                      )
                    }
                    onChange={(checked) => toggleSelectAllVitals(!checked)}
                  />
                </div>
                {vitalListById.map((li, idx) => {
                  if (!li?.vitalId) return null;
                  const isChecked = (
                    Array.isArray(vitalValues?.selectedVitalIds)
                      ? vitalValues.selectedVitalIds
                      : []
                  ).includes(li.vitalId);
                  return (
                    <div key={li.vitalId ?? `vital_${idx}`} className="mt5">
                      <div className="flex-row-ali-cen">
                        <CustomCheckbox
                          checked={!!isChecked}
                          onChange={(checked) =>
                            toggleVitalSelection(li.vitalId, !checked)
                          }
                        />
                        <div className="ml5">
                          <Typography className="txt-semi-bold fs14 black">
                            {li?.vitalName ?? ""}
                          </Typography>
                          <Typography className="font-medium fs12 gray4">
                            {li?.inputType ?? ""}
                          </Typography>
                        </div>
                      </div>
                      <Divider />
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>
          {vitalValues?.group?.vitalGroupId ? (
            <div className="flex1-end mv20">
              <div>
                <CustomButton
                  smallBtn
                  text={isUpdatingVitalGroup() ? "Update" : "Add"}
                  onClick={handleAddVitalGroup}
                  disabled={
                    isUpdatingVitalGroup()
                      ? false
                      : !(
                          Array.isArray(vitalValues?.selectedVitalIds) &&
                          vitalValues.selectedVitalIds.length > 0
                        )
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>
    </>
  );
};

export default DiagnosticTemplateCreate;
